import {USER_KEYS} from "@/api/userApi";
import Loader from "@/components/Common/kpi/Loader";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import useNotificationType from "@/hooks/useNotificationType";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import {useUserId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import useNotificationStore from "@/store/notification";
import type {TNotification} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import dayjs from "dayjs";
import {Fragment, type FC} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router";
import {useEffectOnce} from "react-use";

const NotificationsTab: FC = () => {
  const {t} = useTranslation("notifications");
  const mapNotification = useNotificationType();
  const resetNotificationCounts = useNotificationStore(state => state.resetNotificationCounts);
  const unseenNotificationCount = useNotificationStore(state => state.notificationCounts.notification.unseen);
  const userId = useUserId();
  const {mutate: markAllAsSeen} = useMutation({
    mutationKey: USER_KEYS.mUserNotificationListSeenStatus(userId),
    mutationFn: () => client.put(USER_KEYS.mUserNotificationListSeenStatus(userId)[0]),
    onMutate: resetNotificationCounts,
  });

  const notificationsQuery = useGenericInfiniteQuery<TNotification>({
    queryKey: USER_KEYS.qUserNotificationList(userId),
    enabled: !!userId,
  });

  const {ref} = useInViewWithQuery(notificationsQuery);

  useEffectOnce(() => {
    if (unseenNotificationCount) {
      markAllAsSeen();
    }
  });

  return (
    <div
      className={clsxm(
        "divide-neutral-white-dark divide-y bg-white",
        notificationsQuery.isLoading ? "flex h-full flex-col justify-center" : "",
      )}>
      {notificationsQuery.isLoading && (
        <Loader
          key="notificationInitial"
          styles={{
            loaderClassName: "h-10 w-10",
            containerClassName: "items-center h-full",
          }}
        />
      )}
      <div className="w-full py-5">
        <span className="text-neutral-black border-neutral-white-dark flex border-b px-5 text-xl font-semibold">
          {t("notifications")}
        </span>
      </div>
      {notificationsQuery.data?.map((noti, index, array) => {
        const notificationType = mapNotification(noti.type);

        if (!notificationType) {
          return index === array.length - 1 ? <div ref={ref} /> : null;
        }

        return (
          <Fragment key={noti.id}>
            <Link
              style={{
                textDecoration: "none",
              }}
              to={notificationType.actionUrl(noti.action_params)}
              className={clsxm(
                "flex min-h-fit items-center justify-between gap-10 px-5 py-4",
                noti.seen ? "bg-white" : "bg-soft-grey-100",
                "hover:bg-neutral-white-dark",
              )}>
              <div className="flex flex-col justify-between gap-2">
                <span className="text-neutral-black text-base font-medium">{noti.title}</span>

                <span className="text-neutral-grey-dark text-sm">{noti.body}</span>
                <div className="flex items-center gap-2">
                  <div className="bg-primary-light-solid flex items-center gap-1 rounded-full p-1.5">
                    <div className="size-3 shrink-0">
                      <img src={notificationType.iconSrc} className="size-full object-contain" />
                    </div>
                    <span className={clsxm("text-[10px] leading-none font-bold", notificationType.textClassName)}>
                      {t(notificationType.iconTextTranslation)}
                    </span>
                  </div>
                  <span className="text-neutral-grey-dark p-0 text-[10px] leading-none">
                    {dayjs.utc(noti.created).local().startOf("seconds").fromNow()}
                  </span>
                </div>
              </div>
            </Link>
            {index === array.length - 1 && <div ref={ref} />}
          </Fragment>
        );
      })}
      {notificationsQuery.hasNextPage && (
        <div className="min-h-20">
          {notificationsQuery.isFetchingNextPage && (
            <Loader
              key="notifications"
              styles={{
                containerClassName: "items-center py-5",
                loaderClassName: "size-10",
              }}
            />
          )}
        </div>
      )}

      {!notificationsQuery.isLoading && notificationsQuery.data?.length === 0 && (
        <div className="bg-primary-light-solid mx-5 my-7 rounded-md p-6">
          <span className="my-0 bg-linear-(--brand-medium-gradient) bg-clip-text text-center text-xl font-bold text-transparent">
            {t("notification_tab_empty_state")}
          </span>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
